import { useState, useEffect, useRef, useCallback } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { auth, tts } from '../api/client'

const markdownComponents = {
  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="ml-2">{children}</li>,
}

export default function FloatingVoiceButton() {
  // Core state
  const [isActive, setIsActive] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const [isListening, setIsListening] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isSpeaking, setIsSpeaking] = useState(false)
  const [transcript, setTranscript] = useState('')
  const [lastUserQuery, setLastUserQuery] = useState('')
  const [lastResponse, setLastResponse] = useState('')
  const [conversationId, setConversationId] = useState(null)
  const [speechSupported, setSpeechSupported] = useState(true)
  const [error, setError] = useState(null)

  // Refs
  const wsRef = useRef(null)
  const recognitionRef = useRef(null)
  const audioRef = useRef(null)
  const reconnectTimeoutRef = useRef(null)
  const reconnectAttemptsRef = useRef(0)
  const streamingContentRef = useRef('')
  const autoDismissRef = useRef(null)
  const connectingRef = useRef(false)

  // Check browser support
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SpeechRecognition) {
      setSpeechSupported(false)
    }
  }, [])

  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SpeechRecognition) return

    const recognition = new SpeechRecognition()
    recognition.continuous = false
    recognition.interimResults = true
    recognition.lang = 'en-US'

    recognition.onresult = (event) => {
      let interimTranscript = ''
      let finalTranscript = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        if (result.isFinal) {
          finalTranscript += result[0].transcript
        } else {
          interimTranscript += result[0].transcript
        }
      }

      setTranscript(finalTranscript || interimTranscript)

      if (finalTranscript) {
        sendMessage(finalTranscript)
        setTranscript('')
      }
    }

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error)
      setIsListening(false)
    }

    recognition.onend = () => {
      setIsListening(false)
    }

    recognitionRef.current = recognition

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort()
      }
    }
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close()
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
      if (autoDismissRef.current) {
        clearTimeout(autoDismissRef.current)
      }
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current = null
      }
      window.speechSynthesis?.cancel()
    }
  }, [])

  // Lazy WebSocket connect
  const connectWebSocket = useCallback(async () => {
    if (connectingRef.current || (wsRef.current && wsRef.current.readyState === WebSocket.OPEN)) {
      return true
    }

    connectingRef.current = true

    try {
      const chatToken = await auth.getChatToken()
      if (!chatToken) {
        console.error('Failed to get chat token')
        connectingRef.current = false
        return false
      }

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
      const host = window.location.host
      const wsUrl = `${protocol}//${host}/trainer-chat?token=${chatToken}`

      const ws = new WebSocket(wsUrl)
      wsRef.current = ws

      return new Promise((resolve) => {
        ws.onopen = () => {
          setIsConnected(true)
          connectingRef.current = false
          reconnectAttemptsRef.current = 0
          resolve(true)
        }

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data)

            switch (data.type) {
              case 'message':
              case 'response': {
                const content = data.content || data.message
                setIsProcessing(false)
                streamingContentRef.current = ''
                setLastResponse(content)
                speakText(content)
                break
              }

              case 'typing':
              case 'status':
                if (data.status === 'typing' || data.type === 'typing') {
                  setIsProcessing(true)
                }
                break

              case 'stream':
                setIsProcessing(false)
                streamingContentRef.current += data.content
                setLastResponse(streamingContentRef.current)
                break

              case 'stream_end': {
                const finalContent = streamingContentRef.current + (data.content || '')
                if (finalContent) {
                  streamingContentRef.current = ''
                  setLastResponse(finalContent)
                  speakText(finalContent)
                }
                break
              }

              case 'error':
                console.error('WebSocket error:', data.message)
                setIsProcessing(false)
                setLastResponse('Sorry, something went wrong. Try again.')
                break

              case 'conversation_id':
                setConversationId(data.conversationId)
                break
            }
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error)
          }
        }

        ws.onclose = () => {
          setIsConnected(false)
          connectingRef.current = false
          // Auto-reconnect with exponential backoff (1s, 2s, 4s, 8s, ... max 30s)
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000)
          reconnectAttemptsRef.current += 1
          reconnectTimeoutRef.current = setTimeout(() => {
            if (wsRef.current === ws) {
              connectWebSocket()
            }
          }, delay)
        }

        ws.onerror = (error) => {
          console.error('WebSocket error:', error)
          connectingRef.current = false
          resolve(false)
        }
      })
    } catch (error) {
      console.error('Failed to connect WebSocket:', error)
      connectingRef.current = false
      return false
    }
  }, [])

  function sendMessage(content) {
    if (!content.trim() || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return
    }

    setLastUserQuery(content.trim())
    setLastResponse('')
    setIsProcessing(true)
    streamingContentRef.current = ''

    // Clear any pending auto-dismiss
    if (autoDismissRef.current) {
      clearTimeout(autoDismissRef.current)
      autoDismissRef.current = null
    }

    wsRef.current.send(JSON.stringify({
      type: 'message',
      content: content.trim(),
      conversationId,
    }))
  }

  async function speakText(text) {
    if (!text) return

    stopSpeaking()
    setIsSpeaking(true)

    try {
      const response = await tts.elevenlabs(text)
      if (response.success && response.audio) {
        const audioData = `data:${response.mimeType};base64,${response.audio}`
        const audio = new Audio(audioData)
        audioRef.current = audio

        audio.onended = () => {
          setIsSpeaking(false)
          audioRef.current = null
          // Auto-dismiss overlay 2s after TTS ends
          autoDismissRef.current = setTimeout(() => {
            setIsActive(false)
            setLastResponse('')
            setLastUserQuery('')
            setTranscript('')
          }, 2000)
        }

        audio.onerror = () => {
          setIsSpeaking(false)
          audioRef.current = null
        }

        await audio.play()
      } else {
        console.error('TTS error:', response.error)
        setIsSpeaking(false)
      }
    } catch (error) {
      console.error('TTS request failed:', error)
      setIsSpeaking(false)
    }
  }

  function stopSpeaking() {
    setIsSpeaking(false)
    window.speechSynthesis?.cancel()
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
      audioRef.current = null
    }
  }

  async function handleMicTap() {
    // Clear any pending auto-dismiss
    if (autoDismissRef.current) {
      clearTimeout(autoDismissRef.current)
      autoDismissRef.current = null
    }

    // If currently listening, stop
    if (isListening) {
      recognitionRef.current?.stop()
      setIsListening(false)
      return
    }

    // Stop any ongoing speech
    stopSpeaking()

    // Activate the overlay
    setIsActive(true)

    // Lazy connect WebSocket on first use
    const connected = await connectWebSocket()
    if (!connected && !isConnected) {
      setLastResponse('Could not connect. Please try again.')
      return
    }

    // Start listening
    if (recognitionRef.current) {
      setTranscript('')
      setError(null)
      try {
        recognitionRef.current.start()
        setIsListening(true)
      } catch (e) {
        // Recognition may already be running or permission denied
        console.error('Recognition start error:', e)
        setError('Could not start voice recognition. Please try again.')
        setIsListening(false)
      }
    }
  }

  function handleDismiss() {
    if (autoDismissRef.current) {
      clearTimeout(autoDismissRef.current)
      autoDismissRef.current = null
    }
    stopSpeaking()
    setIsActive(false)
    setLastResponse('')
    setLastUserQuery('')
    setTranscript('')
    setIsProcessing(false)
  }

  // Determine button style
  let buttonClasses = 'w-16 h-16 rounded-full transition-all flex items-center justify-center '
  if (isListening) {
    buttonClasses += 'bg-lume-teal animate-glow-pulse shadow-lume-strong'
  } else if (isProcessing) {
    buttonClasses += 'bg-dial-surface border-2 border-lume-teal shadow-lume'
  } else if (isSpeaking) {
    buttonClasses += 'bg-lume-teal shadow-lume'
  } else {
    buttonClasses += 'bg-dial-surface border-2 border-lume-teal shadow-lume hover:shadow-lume-strong'
  }

  const showOverlay = isActive && (lastResponse || isProcessing || transcript || lastUserQuery)

  return (
    <div className="fixed bottom-6 right-4 z-40 flex flex-col items-end gap-3">
      {/* Response overlay */}
      {showOverlay && (
        <div className="w-72 bg-dial-surface border border-dial-border rounded-2xl p-4 shadow-elevated animate-slide-in">
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute top-2 right-2 p-1 text-indices-muted hover:text-indices-bright transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* User query */}
          {lastUserQuery && (
            <div className="mb-2">
              <p className="text-xs text-indices-muted mb-0.5">You</p>
              <p className="text-sm text-indices-bright">{lastUserQuery}</p>
            </div>
          )}

          {/* Live transcript while listening */}
          {transcript && (
            <div className="mb-2">
              <p className="text-sm text-lume-teal italic">{transcript}</p>
            </div>
          )}

          {/* Processing indicator */}
          {isProcessing && (
            <div className="flex items-center gap-2 py-2">
              <div className="flex items-center space-x-1.5">
                <div className="w-2 h-2 bg-lume-teal rounded-full animate-pulse"></div>
                <div className="w-2 h-2 bg-lume-teal rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 bg-lume-teal rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
              </div>
              <span className="text-xs text-indices-muted">Thinking...</span>
            </div>
          )}

          {/* AI response */}
          {lastResponse && !isProcessing && (
            <div>
              {lastUserQuery && <div className="border-t border-dial-border my-2"></div>}
              <div className="text-sm text-lume-teal prose prose-sm prose-invert max-w-none">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={markdownComponents}
                >
                  {lastResponse}
                </ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Main mic button */}
      <button
        onClick={speechSupported ? handleMicTap : () => setError('Voice input is not supported in this browser. Try Chrome or Safari.')}
        className={buttonClasses}
        title={speechSupported ? (isListening ? 'Stop listening' : 'Tap to speak') : 'Voice not supported in this browser'}
      >
          {isProcessing ? (
            // Spinner
            <svg className="w-7 h-7 text-lume-teal animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
          ) : isSpeaking ? (
            // Speaker icon
            <svg className="w-7 h-7 text-dial-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
            </svg>
          ) : (
            // Mic icon
            <svg className={`w-7 h-7 ${isListening ? 'text-dial-black' : 'text-lume-teal'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
            </svg>
          )}
        </button>

      {/* Error toast */}
      {error && !isActive && (
        <div className="absolute bottom-20 right-0 w-64 bg-dial-surface border border-status-error/50 rounded-lg p-3 shadow-elevated animate-slide-in">
          <p className="text-xs text-status-error">{error}</p>
        </div>
      )}
    </div>
  )
}
